// src/utils/auth.js
import { jwtDecode } from "jwt-decode";

import { API_URL } from "../config";

/* ---------- хелперы хранения токенов ---------- */
export const getAccessToken = () => localStorage.getItem("access_token");

function emitTokensChanged() {
  try { window.dispatchEvent(new Event("auth:tokens-changed")); } catch {}
}

export const saveAccessToken = (t) => {
  localStorage.setItem("access_token", t);
  emitTokensChanged();
};
export const removeAccessToken = () => {
  localStorage.removeItem("access_token");
  emitTokensChanged();
};

export const getRefreshToken = () => localStorage.getItem("refresh_token");
export const saveRefreshToken = (t) => {
  localStorage.setItem("refresh_token", t);
  emitTokensChanged();
};
export const removeRefreshToken = () => {
  localStorage.removeItem("refresh_token");
  emitTokensChanged();
};


/* ---------- проверка истечения токена ---------- */
export function isExpired(token) {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    // небольшой зазор, чтобы не попасть в гонку на последней секунде
    return !exp || Date.now() / 1000 >= exp - 5;
  } catch {
    return true;
  }
}

const LOGOUT_REASON_MESSAGES = {
  refresh_expired: "Токен авторизации просрочен",
  no_refresh: "Токен авторизации отсутствует",
  license_limit_exceeded:
    "Превышен лимит активных устройств по лицензии. Закройте приложение на другом устройстве и войдите снова. Или приобретите дополнительный пакет лицензий",
  license_invalid: "Лицензия недействительна или доступ запрещён",
};

// Кэш живёт только в рамках текущей загрузки страницы:
// переживает ремоунт Login, но сбрасывается при F5.
let logoutNoticeCached = null;
let logoutNoticeResolved = false;

function isPageReload() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

export function getLogoutNoticeMessage() {
  if (logoutNoticeResolved) {
    return logoutNoticeCached || "";
  }

  logoutNoticeResolved = true;

  if (isPageReload()) {
    localStorage.removeItem("logout_reason");
    logoutNoticeCached = "";
    return "";
  }

  const reason = localStorage.getItem("logout_reason");
  if (!reason) {
    logoutNoticeCached = "";
    return "";
  }

  const message = LOGOUT_REASON_MESSAGES[reason] || "";
  logoutNoticeCached = message;
  localStorage.removeItem("logout_reason");
  return message;
}

export function clearLogoutNotice() {
  localStorage.removeItem("logout_reason");
  logoutNoticeCached = "";
}

/* ---------- единый выход ---------- */
let logoutInProgress = false;
let logoutPromise = null;

export const isLogoutInProgress = () => logoutInProgress;

export async function serverLogout() {
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: "POST",
      credentials: "include", // ← обязательно, чтобы cookie пришла на бэк
      keepalive: true,
    });
  } catch {}
}

export async function logout(reason) {
  if (logoutPromise) return logoutPromise;

  logoutInProgress = true;
  logoutPromise = (async () => {
    await serverLogout();

    removeAccessToken();
    removeRefreshToken();
    localStorage.removeItem("employee_id");

    const persistedReasons = new Set([
      "refresh_expired",
      "no_refresh",
      "license_limit_exceeded",
      "license_invalid",
    ]);

    if (persistedReasons.has(reason)) {
      localStorage.setItem("logout_reason", reason);
    } else {
      localStorage.removeItem("logout_reason");
    }

    try {
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    } catch {}
  })();

  return logoutPromise;
}

/* ---------- один общий промис, чтобы не было параллельных refresh ---------- */
let refreshingPromise = null;

/* ---------- запрос обновления токена ---------- */
export async function refreshAccessToken() {
  if (refreshingPromise) return refreshingPromise;

  // ⚠️ Тело отправляем только если refresh найден в LS (переходный период).
  const refreshFromLS = getRefreshToken();

  refreshingPromise = fetch(`${API_URL}/auth/jwt/refresh`, {
    method: "POST",
    credentials: "include", // <-- берём refresh из HttpOnly cookie
    headers: refreshFromLS ? { "Content-Type": "application/json" } : undefined,
    body: refreshFromLS ? JSON.stringify({ refresh_token: refreshFromLS }) : undefined,
  })
    .then(async (res) => {
      if (!res.ok) {
        let text = "";
        try { text = await res.text(); } catch {}
        const err = new Error(`refresh-failed:${res.status}`);
        err.status = res.status;
        err.body = text;
        throw err;
      }

      const data = await res.json();

      if (data.access_token) saveAccessToken(data.access_token);
      // Переходный период: если бэк вернул новый refresh, зеркалим в LS
      if (data.refresh_token) saveRefreshToken(data.refresh_token);

      return data.access_token;
    })
    .finally(() => {
      refreshingPromise = null;
    });

  return refreshingPromise;
}

/* ---------- НОВОЕ: bootstrap cookie для старых сессий ----------
   Проблема: раньше access мог быть только в LS и оставаться валидным,
   поэтому refresh не вызывался и access_cookie не появлялся.
   Решение: один раз за сессию вкладки делаем мягкий refresh, чтобы бэк
   выставил HttpOnly access_token cookie (и дальше медиа видно сразу).
*/
export async function bootstrapAccessCookie() {
  try {
    if (sessionStorage.getItem("cookie_bootstrap_done") === "1") return;

    // Если нет вообще никаких токенов в LS — нечего бутстрапить
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (!access && !refresh) {
      sessionStorage.setItem("cookie_bootstrap_done", "1");
      return;
    }

    // Делает запрос refresh (возьмёт refresh из cookie или из LS body),
    // а сервер выставит Set-Cookie для access_token.
    // Ошибки игнорируем: если refresh протух — просто не сможем поднять cookie.
    await refreshAccessToken();
  } catch {
    // ignore
  } finally {
    try { sessionStorage.setItem("cookie_bootstrap_done", "1"); } catch {}
  }
}

/* ---------- обёртка над fetch с авторизацией и авто-рефрешем ---------- */
export async function apiFetch(input, init = {}) {
  let token = getAccessToken();

  // Если токена нет или он истёк — пытаемся рефрешнуть (cookie/LS)
  if (!token || isExpired(token)) {
    try {
      token = await refreshAccessToken();
    } catch {
      throw new Error("unauthorized");
    }
  }

  const headers = new Headers(init.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // первый запрос — всегда с куками
  let res = await fetch(input, { ...init, headers, credentials: "include" });

  // если 401 — пробуем обновить токен и повторить один раз
  if (res.status === 401) {
    try {
      const newToken = await refreshAccessToken();

      const retryHeaders = new Headers(init.headers || {});
      retryHeaders.set("Authorization", `Bearer ${newToken}`);
      if (!retryHeaders.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
        retryHeaders.set("Content-Type", "application/json");
      }
      res = await fetch(input, { ...init, headers: retryHeaders, credentials: "include" });
    } catch {
      throw new Error("unauthorized");
    }
  }

  return res;
}

/* ---------- сохранение employee_id ---------- */
export async function saveEmployeeId() {
  const res = await apiFetch(`${API_URL}/users/me`);
  if (!res.ok) return;
  const user = await res.json();
  if (user?.employee_id != null) {
    localStorage.setItem("employee_id", user.employee_id);
    return user.employee_id;
  }
}

/* ---------- полезный хелпер, если где-то нужен id из токена ---------- */
export function getUserIdFromToken() {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.sub ? Number(decoded.sub) : null;
  } catch {
    return null;
  }
}

/* ---------- НОВОЕ: passwordless по e-mail ---------- */
export async function requestEmailCode(email) {
  const res = await fetch(`${API_URL}/auth/email/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    let msg = "Не удалось отправить код";
    try {
      const data = await res.json();
      msg = data?.detail || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return true;
}

export async function verifyEmailCode(email, code) {
  const res = await fetch(`${API_URL}/auth/email/verify`, {
    method: "POST",
    credentials: "include", // ← примем HttpOnly refresh-cookie
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    let msg = "Код недействителен";
    try {
      const data = await res.json();
      msg = data?.detail || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const data = await res.json();
  if (data.access_token) saveAccessToken(data.access_token);
  if (data.refresh_token) saveRefreshToken(data.refresh_token);
  return data;
}